import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CURRENCY_DISPLAY_ORDER } from "@/helpers/currencyPresentation"
import { resolveItemImage } from "@/helpers/itemImage"
import { useCatalogs } from "@/hooks/useCatalogs"
import {
  buildRewardCurrencies,
  isRewardNothing
} from "@/hooks/useMobReward/helpers"
import {
  IMobRewardCurrency,
  IMobRewardDrop,
  IMobRewardView
} from "@/hooks/useMobReward/types"
import { useGetMobKillQuery } from "@/store/apis/mobsAPI/mobs.api"

const EMPTY_DROPS: IMobRewardDrop[] = []
const EMPTY_CURRENCIES: IMobRewardCurrency[] = []

export const useMobReward = (killId: string): IMobRewardView => {
  const { kill, isError } = useGetMobKillQuery(killId, {
    selectFromResult: ({ currentData, isError }) => ({
      kill: currentData,
      isError
    })
  })
  const { currencies: catalogCurrencies } = useCatalogs()
  const { i18n } = useTranslation()
  const locale = i18n.language

  const drops = useMemo(
    () =>
      kill
        ? kill.drops.map(
            (drop): IMobRewardDrop => ({
              itemCode: drop.itemCode,
              rarityCode: drop.rarityCode,
              quantity: drop.quantity,
              iconUrl: resolveItemImage(drop.itemCode)
            })
          )
        : EMPTY_DROPS,
    [kill]
  )
  const currencies = useMemo(
    () =>
      kill
        ? buildRewardCurrencies(
            kill.currencyDrops,
            catalogCurrencies,
            locale,
            CURRENCY_DISPLAY_ORDER
          )
        : EMPTY_CURRENCIES,
    [kill, catalogCurrencies, locale]
  )
  const isNothing = isRewardNothing(drops, currencies)

  if (!kill) {
    return {
      status: isError ? "error" : "loading",
      typeCode: "",
      level: 0,
      xp: 0,
      drops,
      currencies,
      isNothing
    }
  }

  return {
    status: "ready",
    typeCode: kill.typeCode,
    level: kill.level,
    xp: kill.xpGranted,
    drops,
    currencies,
    isNothing
  }
}
